import React, { useEffect, useId, useRef, useState } from 'react';
import type { Message } from '../types';
import AudioPlayer from './AudioPlayer';
import {
  CACHED_TRANSCRIPT_DELAY_MS,
  getAudioTranscriptAvailability,
  getTranscriptPreview
} from '../utils/audioTranscript';

interface ChatAudioAttachmentProps {
  message: Message & { audioUrl: string };
  isUser: boolean;
}

const ChatAudioAttachment: React.FC<ChatAudioAttachmentProps> = ({ message, isUser }) => {
  const [requested, setRequested] = useState(false);
  const [delayElapsed, setDelayElapsed] = useState(true);
  const [blocks, setBlocks] = useState(1);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const focusFrameRef = useRef<number | null>(null);
  const contentId = useId();
  const availability = getAudioTranscriptAvailability(message);
  const loading = requested && (availability === 'pending' || (availability === 'ready' && !delayElapsed));
  const visible = requested && availability === 'ready' && delayElapsed;
  const unavailable = requested && availability === 'unavailable';
  const preview = visible ? getTranscriptPreview(message.audioTranscriptForMemory || '', blocks) : null;
  const actionClass = 'min-h-8 rounded px-1 text-xs font-medium focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-current';

  useEffect(() => () => {
    if (timerRef.current !== null) clearTimeout(timerRef.current);
    if (focusFrameRef.current !== null) cancelAnimationFrame(focusFrameRef.current);
  }, []);

  const reveal = () => {
    if (requested) return;
    setRequested(true);
    setBlocks(1);
    const cached = availability === 'ready';
    setDelayElapsed(!cached);
    // Presentation delay only: the canonical transcript comes from the chat listener.
    if (cached) {
      timerRef.current = setTimeout(() => {
        timerRef.current = null;
        setDelayElapsed(true);
      }, CACHED_TRANSCRIPT_DELAY_MS);
    }
  };

  const hide = () => {
    if (timerRef.current !== null) clearTimeout(timerRef.current);
    timerRef.current = null;
    setRequested(false);
    setDelayElapsed(true);
    setBlocks(1);
    focusFrameRef.current = requestAnimationFrame(() => {
      triggerRef.current?.focus();
      focusFrameRef.current = null;
    });
  };

  const showMore = (event: React.MouseEvent<HTMLButtonElement>) => {
    const nextBlocks = blocks + 1;
    if (!getTranscriptPreview(message.audioTranscriptForMemory || '', nextBlocks).hasMore
      && document.activeElement === event.currentTarget) {
      triggerRef.current?.focus();
    }
    setBlocks(nextBlocks);
  };

  return (
    <div className="mt-1 min-w-0">
      <AudioPlayer src={message.audioUrl} isUser={isUser} />
      {isUser && (
        <div className="text-white">
          <span className="sr-only" role="status">
            {loading ? 'Transcribiendo el audio.' : visible ? 'Transcripción lista.' : ''}
          </span>
          <div id={contentId} hidden={!requested} aria-busy={loading}>
            {preview && (
              <p className="pt-2 text-sm leading-relaxed whitespace-pre-wrap break-words [overflow-wrap:anywhere]">
                “{preview.text}{preview.hasMore ? '…' : ''}”
              </p>
            )}
            {unavailable && (
              <p className="pt-2 text-xs leading-relaxed" role="status">
                {message.generationStatus === 'failed'
                  ? 'No se pudo transcribir el audio. Reintenta el envío del mensaje.'
                  : message.generationStatus === 'canceled'
                    ? 'La transcripción no está disponible porque se canceló el envío.'
                    : 'No hay una transcripción disponible para este audio.'}
              </p>
            )}
          </div>
          <div className={`flex items-center gap-3 ${visible || unavailable ? 'justify-end' : 'justify-start'}`}>
            <button
              ref={triggerRef}
              type="button"
              onClick={visible || unavailable ? hide : reveal}
              aria-disabled={loading || undefined}
              aria-expanded={requested}
              aria-controls={contentId}
              className={`${actionClass} inline-flex items-center gap-2 text-left`}
            >
              {loading && <span aria-hidden="true" className="h-3 w-3 shrink-0 rounded-full border-2 border-line/40 border-t-white motion-safe:animate-spin" />}
              {loading ? 'Transcribiendo...' : visible || unavailable ? 'Ocultar' : 'Transcribir'}
            </button>
            {preview?.hasMore && (
              <button type="button" onClick={showMore} className={actionClass} aria-controls={contentId}>
                Ver más
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ChatAudioAttachment;
